import { BaseDataProvider } from "./baseProvider";
import type {
  PropertyListParams,
  PropertyVisibilityUpdate,
} from "../types/api";

export class PropertyDataProvider extends BaseDataProvider {
  constructor() {
    super("properties");
  }

  async getList(_resource: string, params: PropertyListParams) {
    const { page, perPage } = params.pagination || { page: 1, perPage: 20 };
    const filter = params.filter || {};

    // Ensure limit doesn't exceed API maximum (100)
    const limit = Math.min(perPage, 100);

    // Build query parameters
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    // Add optional filters
    if (filter.search) {
      queryParams.append("search", filter.search);
    }
    if (filter.includeDeleted !== undefined) {
      queryParams.append("includeDeleted", filter.includeDeleted.toString());
    }
    if (filter.includeClosed !== undefined) {
      queryParams.append("includeClosed", filter.includeClosed.toString());
    }
    if (filter.regionId) {
      queryParams.append("regionId", filter.regionId);
    }
    if (filter.starRating) {
      queryParams.append("starRating", filter.starRating);
    }
    if (filter.kind) {
      queryParams.append("kind", filter.kind);
    }

    const endpoint = `properties?${queryParams.toString()}`;
    const response = (await this.makeRequest(endpoint)) as {
      properties: unknown[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };

    // Transform API response to React Admin format
    return {
      data: response.properties || [],
      total: response.total || 0,
      pageInfo: {
        hasNextPage: response.page < response.totalPages,
        hasPreviousPage: response.page > 1,
      },
    };
  }

  async getOne(_resource: string, params: Record<string, unknown>) {
    const endpoint = `properties/${params.id}`;
    const response = await this.makeRequest(endpoint);
    return { data: response };
  }

  async getMany(_resource: string, params: Record<string, unknown>) {
    // For getMany, we'll fetch each property individually
    // This could be optimized with a batch endpoint if available
    const promises = (params.ids as string[]).map((id) =>
      this.getOne(_resource, { id }),
    );
    const results = await Promise.all(promises);
    return { data: results.map((result) => result.data) };
  }

  async getManyReference() {
    // This would typically be used for related resources
    // For now, we'll return an empty result
    return {
      data: [],
      total: 0,
      pageInfo: {
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  }

  // Properties are read-only for create/update/delete operations
  // These operations are not supported by the API
  async create() {
    throw new Error("Creating properties is not supported via the admin API");
  }


  async updateMany() {
    throw new Error(
      "Bulk updating properties is not supported via the admin API",
    );
  }

  async delete() {
    throw new Error("Deleting properties is not supported via the admin API");
  }

  async deleteMany() {
    throw new Error(
      "Bulk deleting properties is not supported via the admin API",
    );
  }

  // Custom methods for property-specific operations
  async updateVisibility(
    propertyId: string,
    visibilityData: PropertyVisibilityUpdate,
  ) {
    const endpoint = `properties/${propertyId}/visibility`;
    const response = await this.makeRequest(endpoint, {
      method: "PUT",
      body: JSON.stringify(visibilityData),
      headers: new Headers({
        "Content-Type": "application/json",
      }),
    });
    return response;
  }

  // Override update method to handle visibility updates
  async update(_resource: string, params: Record<string, unknown>) {
    const { id, data } = params;
    
    // Check if this is a visibility update
    if (data && typeof data === 'object' && 'visibility' in data) {
      const visibilityData = (data as { visibility: PropertyVisibilityUpdate }).visibility;
      return this.updateVisibility(id as string, visibilityData);
    }
    
    // For other updates, throw error as they're not supported
    throw new Error("Updating properties is not supported via the admin API");
  }

  async getPropertyStats() {
    const endpoint = "properties/stats/overview";
    const response = await this.makeRequest(endpoint);
    return response;
  }
}
